import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState, useEffect, useRef } from 'react';

const useCountUp = (end: number, duration: number = 2000, suffix: string = '') => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number | null = null;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  return { count, ref, suffix };
};

const popularExperiences = [
  {
    id: 1,
    title: '아모레퍼시픽 K-뷰티 체험',
    category: 'K-뷰티',
    description: 'K-뷰티 대표 기업 아모레퍼시픽에서 메이크업 체험 및 브랜드 스토리를 경험합니다.',
    image: 'https://public.readdy.ai/ai/img_res/574709ba9fb179fa6d8e4164a3fa0f6c.jpg',
    link: '/experience#korea-experience',
    tag: '서울 용산구'
  },
  {
    id: 2,
    title: 'Gen.G 이스포츠 직업 탐색',
    category: 'K-엔터테인먼트',
    description: '글로벌 이스포츠 기업 Gen.G에서 게임 산업과 직업 세계를 탐색합니다.',
    image: 'https://readdy.ai/api/search-image?query=Gen%20G%20esports%20gaming%20center%20in%20seoul%20with%20high-end%20gaming%20setups%20and%20professional%20esports%20arena%2C%20modern%20gaming%20facility%20with%20LED%20lighting%20and%20multiple%20screens%2C%20professional%20esports%20environment%2C%20clean%20and%20impressive%20gaming%20venue%20interior%2C%20career%20exploration%20in%20gaming%20industry&width=600&height=400&seq=popular-exp-geng-002&orientation=landscape',
    link: '/experience#korea-experience',
    tag: '서울 강남구'
  },
  {
    id: 3,
    title: '시사쿡요리학원 요리 실습',
    category: 'K-푸드',
    description: '전문 셰프와 함께 한국 전통 요리를 직접 만들어보는 실습 체험 프로그램입니다.',
    image: 'https://readdy.ai/api/search-image?query=professional%20korean%20cooking%20academy%20classroom%20with%20modern%20kitchen%20equipment%20and%20stainless%20steel%20counters%2C%20students%20in%20white%20aprons%20learning%20to%20cook%20traditional%20korean%20food%2C%20bright%20clean%20culinary%20school%20interior%2C%20professional%20educational%20photography%2C%20hands-on%20cooking%20class%20atmosphere&width=600&height=400&seq=popular-exp-cook-003&orientation=landscape',
    link: '/experience#korea-experience',
    tag: '서울 강남구'
  },
  {
    id: 4,
    title: '명동 K-뷰티 거리 체험',
    category: 'K-뷰티',
    description: '한국 뷰티 브랜드가 밀집한 명동에서 최신 K-뷰티 트렌드를 직접 체험합니다.',
    image: 'https://readdy.ai/api/search-image?query=vibrant%20korean%20cosmetics%20shopping%20street%20in%20myeongdong%20seoul%20with%20colorful%20store%20fronts%20and%20beauty%20product%20displays%2C%20busy%20shopping%20district%20with%20modern%20signage%2C%20bright%20daytime%20atmosphere%2C%20professional%20travel%20photography%2C%20iconic%20korean%20beauty%20shopping%20destination&width=600&height=400&seq=popular-exp-myeong-006&orientation=landscape',
    link: '/experience#korea-experience',
    tag: '서울 중구 명동'
  },
  {
    id: 5,
    title: 'K-뷰티 전문 스튜디오',
    category: 'K-뷰티',
    description: '전문 강사와 함께 한국 뷰티 트렌드를 배우고 직접 체험하는 실습 중심 프로그램입니다.',
    image: 'https://readdy.ai/api/search-image?query=modern%20korean%20beauty%20salon%20and%20cosmetics%20studio%20interior%20with%20bright%20lighting%20and%20professional%20makeup%20stations%2C%20clean%20white%20and%20rose%20gold%20aesthetic%2C%20beauty%20products%20displayed%20on%20shelves%2C%20professional%20educational%20beauty%20workshop%20setting%2C%20warm%20inviting%20atmosphere&width=600&height=400&seq=korea-place-beauty-studio-002&orientation=landscape',
    link: '/experience#korea-experience',
    tag: '서울 강남구'
  },
  {
    id: 6,
    title: '광장시장 K-푸드 투어',
    category: 'K-푸드',
    description: '한국 전통 시장에서 다양한 길거리 음식을 맛보며 한국 식문화를 체험합니다.',
    image: 'https://readdy.ai/api/search-image?query=traditional%20korean%20food%20market%20gwangjang%20market%20in%20seoul%20with%20colorful%20street%20food%20stalls%20and%20vendors%2C%20vibrant%20market%20atmosphere%20with%20bindaetteok%20and%20tteokbokki%20displays%2C%20authentic%20korean%20culinary%20culture%2C%20professional%20food%20photography%2C%20warm%20and%20lively%20market%20scene&width=600&height=400&seq=korea-place-market-002&orientation=landscape',
    link: '/experience#korea-experience',
    tag: '서울 종로구'
  },
];

const HomePage = () => {
  const [activeProgram, setActiveProgram] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [selectedReview, setSelectedReview] = useState<number | null>(null);
  const [reviewSlideIndex, setReviewSlideIndex] = useState(0);
  const slidesPerView = 3;
  const maxIndex = popularExperiences.length - slidesPerView;

  const stat1 = useCountUp(60000, 2000);
  const stat2 = useCountUp(100, 1800);
  const stat3 = useCountUp(500, 2200);
  const stat4 = useCountUp(98, 2000);

  const reviews = [
    {
      id: 1,
      rating: 5,
      content: '일일 뷰티 클래스를 받고 왔는데 운영하시는 강사님도 매우 친절하시고, 무엇보다 신청 과정부터 프로그램 일정 당일 안내까지 상세하게 안내해주셔서 다른 어플과 차별점을 느낄 수 있었어요. 소중한 추억을 쌓았습니다. 정말 감사합니다.',
      name: '이**',
      role: '개인 신청자'
    },
    {
      id: 2,
      rating: 5,
      content: '일본 학생들을 데리고 어디를 가야할지 고민이 많았었는데 이러한 고민들을 어떻게 아셨을까요. 브란치에서 척척 계획을 세워주시고, 학생들이 뷰티와 케이팝에 관심이 많았는데 덕분에 한국 문화를 제대로 경험할 수 있었습니다. 앞으로도 잘 부탁드리겠습니다.',
      name: '나카**',
      role: '유학원 대표'
    },
    {
      id: 3,
      rating: 5,
      content: '자유학기제 대상 학생들을 데리고 브란치를 통해서 아모레퍼시픽과 시사쿡요리학원 외 4개 기업체를 방문했습니다. 전문 강사님들 모두 노련하게 강의를 진행해주시고, 직업별로 유익한 실무 체험들까지 할 수 있어서 정말 좋았습니다. 특히 아모레 퍼시픽 메이크업 체험은 대단히 만족스럽습니다!!',
      name: '심**',
      role: '00중 담임교사'
    },
    {
      id: 4,
      rating: 5,
      content: '4, 5학년 대상으로 진로콘서트를 연계하였는데 직업인 강사분들의 강의 역량이 매우 좋았고 프로그램 운영 준비도 체계적으로 진행해주셔서 학교에서는 크게 신경 쓸 부분이 없었습니다. 학생들의 반응도 좋아서 다음 학기에도 다시 연계할 예정입니다.',
      name: '김**',
      role: '00초 진로전담교사'
    },
    {
      id: 5,
      rating: 5,
      content: '대표님께서 진로교육 분야에서 오랜 기간 활동하신 경험이 있으셔서 프로그램 협의 과정이 매우 수월했습니다. 학교 상황에 맞게 프로그램을 조율해주셨고 강사분들의 전문성도 높아 학생들의 만족도가 매우 높았습니다.',
      name: '이**',
      role: '000중 진로진학교사'
    },
    {
      id: 6,
      rating: 5,
      content: '대표님의 진로특강을 신청하여 진행했는데 강의 준비가 정말 체계적으로 되어 있어 인상 깊었습니다. 학생들이 앞으로 준비해야 할 역량과 미래 직업 변화에 대해 현실적으로 설명해주셔서 학생들에게 많은 도움이 된 시간이었습니다.',
      name: '박**',
      role: '000고 진로진학상담교사'
    },
    {
      id: 7,
      rating: 5,
      content: '학생들이 직접 직업인과 만나 이야기를 듣고 활동까지 해볼 수 있어 일반적인 진로 수업보다 훨씬 몰입도가 높았습니다. 체험 중심 프로그램이라 학생들의 참여도가 높았고 진로에 대해 생각해볼 수 있는 좋은 기회였습니다.',
      name: '최**',
      role: '00중 진로부장교사'
    },
    {
      id: 8,
      rating: 5,
      content: '체험 장소에 직접 방문하여 진행하는 원데이클래스 프로그램이어서 학생들에게 실제 현장을 경험하게 해줄 수 있어 매우 좋았습니다. 단순한 체험이 아니라 직업에 대한 이해까지 함께 할 수 있는 프로그램이라 만족도가 높았습니다.',
      name: '정**',
      role: '00고 진로진학상담교사'
    },
    {
      id: 9,
      rating: 5,
      content: '학생들이 평소 궁금해하던 직업 이야기를 실제 현직자에게 들을 수 있어 매우 의미 있는 시간이었습니다. 강사분들의 경험담이 현실적이어서 학생들이 진로를 고민하는 데 많은 도움이 되었던 것 같습니다.',
      name: '강**',
      role: '00중 진로전담교사'
    },
    {
      id: 10,
      rating: 5,
      content: '창업 관련 프로그램을 진행했는데 학생들이 팀별로 아이디어를 생각하고 사업계획을 만들어보는 활동이 매우 흥미로웠습니다. 학생들이 적극적으로 참여하는 모습이 인상적이었고 교육적인 효과도 매우 좋았습니다.',
      name: '윤**',
      role: '00고 창의융합부교사'
    },
    {
      id: 11,
      rating: 5,
      content: '강사분들이 실제 직무 경험을 바탕으로 강의를 해주셔서 학생들이 현실적인 진로 이야기를 들을 수 있었습니다. 교실에서 듣기 어려운 생생한 이야기들이 많아 학생들의 집중도가 매우 높았습니다.',
      name: '장**',
      role: '00중 진로진학교사'
    },
    {
      id: 12,
      rating: 5,
      content: '국제교류 프로그램을 통해 학생들이 해외 문화와 다양한 직업 환경을 경험할 수 있어 의미 있는 시간이었습니다. 단순한 체험이 아니라 교육적인 프로그램으로 구성되어 있어 학교에서도 매우 만족했습니다.',
      name: '조**',
      role: '00고 진로진학상담교사'
    },
    {
      id: 13,
      rating: 5,
      content: '여러 진로 프로그램을 운영해봤지만 브란치는 프로그램 기획부터 운영까지 체계적으로 준비되어 있어 학교 입장에서 매우 편했습니다. 강사 섭취와 운영 관리가 잘 되어 있어 신뢰하고 진행할 수 있는 기관이라고 생각합니다.',
      name: '한**',
      role: '00중 진로교육담당교사'
    }
  ];

  const reviewsPerView = 3;
  const maxReviewIndex = reviews.length - reviewsPerView;

  // 자동 슬라이드 기능 추가
  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => {
        if (prev >= maxIndex) {
          return 0;
        }
        return prev + 1;
      });
    }, 7000); // 7초마다 자동 전환

    return () => clearInterval(interval);
  }, [maxIndex]);

  // 리뷰 자동 슬라이드 기능 추가
  useEffect(() => {
    const reviewInterval = setInterval(() => {
      setReviewSlideIndex((prev) => {
        if (prev >= maxReviewIndex) {
          return 0;
        }
        return prev + 1;
      });
    }, 7000); // 7초마다 자동 전환

    return () => clearInterval(reviewInterval);
  }, [maxReviewIndex]);

  const handlePrev = () => setSlideIndex((prev) => Math.max(prev - 1, 0));
  const handleNext = () => setSlideIndex((prev) => Math.min(prev + 1, maxIndex));

  const handleReviewPrev = () => setReviewSlideIndex((prev) => Math.max(prev - 1, 0));
  const handleReviewNext = () => setReviewSlideIndex((prev) => Math.min(prev + 1, maxReviewIndex));

  const corePrograms = [
    {
      id: 1,
      title: '원데이클래스',
      subtitle: 'One-day Class (현장방문형)',
      description: '체험 장소에 직접 방문하여 현장 라운딩과 전문 직업인 멘토의 지도를 통해 심층적인 직업체험을 진행합니다',
      icon: 'ri-building-line',
      image: 'https://readdy.ai/api/search-image?query=korean%20high%20school%20students%20visiting%20professional%20workplace%20for%20career%20experience%20program%2C%20students%20wearing%20visitor%20badges%20observing%20workplace%20with%20mentor%20guidance%2C%20bright%20modern%20office%20environment%20with%20natural%20lighting%2C%20professional%20educational%20documentary%20photography%2C%20authentic%20career%20exploration%20moment&width=900&height=1200&seq=oneday-class-hero-001&orientation=portrait'
    },
    {
      id: 2,
      title: '진로콘서트',
      subtitle: 'Career Concert (학교&기업 방문형)',
      description: '전문 직업인 강사가 학교 또는 기업 현장으로 직접 방문하여 학급별로 다양한 직업을 체험할 수 있도록 지원합니다',
      icon: 'ri-presentation-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20career%20mentor%20giving%20interactive%20workshop%20to%20korean%20high%20school%20students%20in%20bright%20modern%20classroom%2C%20students%20engaged%20in%20hands-on%20career%20activities%20with%20enthusiasm%2C%20warm%20educational%20atmosphere%20with%20natural%20lighting%2C%20professional%20documentary%20photography%20style&width=900&height=1200&seq=career-concert-hero-001&orientation=portrait'
    },
    {
      id: 3,
      title: '국제교류 프로그램',
      subtitle: 'International Exchange',
      description: '한국과 일본을 오가며 문화·산업·진로를 체험하고 글로벌 역량을 키웁니다',
      icon: 'ri-global-line',
      image: 'https://readdy.ai/api/search-image?query=diverse%20group%20of%20korean%20high%20school%20students%20in%20school%20uniforms%20smiling%20brightly%20and%20looking%20at%20camera%20together%2C%20standing%20in%20front%20of%20modern%20bright%20school%20building%20entrance%2C%20warm%20sunny%20day%20with%20soft%20natural%20light%2C%20students%20showing%20genuine%20happiness%20and%20friendship%2C%20clean%20and%20bright%20background%20with%20soft%20bokeh%2C%20professional%20portrait%20photography%20style%2C%20positive%20and%20energetic%20youth%20atmosphere%2C%20east%20asian%20students%20only&width=900&height=1200&seq=hero-korean-students-new-005&orientation=portrait'
    },
    {
      id: 4,
      title: '진로 특강',
      subtitle: 'Career Seminar',
      description: '미래 산업과 진로 환경을 이해하고 자기 성찰을 통해 방향을 설정합니다',
      icon: 'ri-lightbulb-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20career%20seminar%20scene%20with%20korean%20high%20school%20students%20sitting%20in%20bright%20modern%20classroom%20listening%20attentively%20to%20speaker%2C%20students%20wearing%20school%20uniforms%20taking%20notes%20and%20engaged%20in%20learning%2C%20natural%20window%20lighting%20creating%20warm%20educational%20atmosphere%2C%20professional%20documentary%20photography%20style%2C%20inspiring%20and%20focused%20learning%20environment%2C%20authentic%20educational%20moment%2C%20korean%20students%20only&width=800&height=1000&seq=program-career-korean-003&orientation=portrait'
    },
    {
      id: 5,
      title: '대학 탐방',
      subtitle: 'University Tour',
      description: '국내외 대학 캠퍼스를 방문하며 전공과 진로를 탐색합니다',
      icon: 'ri-school-line',
      image: 'https://readdy.ai/api/search-image?query=korean%20high%20school%20students%20walking%20through%20beautiful%20modern%20university%20campus%20with%20contemporary%20architecture%20in%20seoul%2C%20students%20wearing%20casual%20clothes%20looking%20at%20campus%20buildings%20with%20interest%20and%20curiosity%2C%20bright%20sunny%20day%20with%20green%20trees%20and%20open%20spaces%2C%20professional%20educational%20photography%2C%20inspiring%20academic%20atmosphere%2C%20natural%20and%20authentic%20moment%20of%20campus%20exploration%20and%20future%20planning%2C%20korean%20students%20only&width=800&height=1000&seq=program-university-korean-002&orientation=portrait'
    },
    {
      id: 6,
      title: '창업 & 자격증 취득과정',
      subtitle: 'Startup & Certification',
      description: '창업 아이디어 기획부터 실전 프로젝트까지 경험하고, 분야별 자격증 취득을 통해 실질적인 역량을 갖춥니다',
      icon: 'ri-lightbulb-flash-line',
      image: 'https://readdy.ai/api/search-image?query=young%20korean%20entrepreneurs%20and%20students%20working%20on%20startup%20business%20ideas%20in%20modern%20bright%20coworking%20space%2C%20brainstorming%20with%20laptops%20and%20whiteboards%2C%20professional%20mentors%20guiding%20project%20development%2C%20innovative%20and%20creative%20atmosphere%20with%20natural%20lighting%2C%20professional%20documentary%20photography%20style%2C%20entrepreneurship%20and%20skill%20development%20theme&width=900&height=1200&seq=main-program-startup-006&orientation=portrait'
    }
  ];

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-homepage';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Branch(브란치) | 글로벌 역량 교육 국제교류 진로 탐색 전문 기업",
        "description": "Branch(브란치)는 서울 강동구에 위치한 글로벌 역량 교육, 국제교류 체험 프로그램, 대학 탐방, 진로 특강을 제공하는 교육 전문 기업입니다.",
        "url": siteUrl + "/",
        "inLanguage": "ko-KR",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Branch(브란치)",
          "url": siteUrl
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "홈", "item": siteUrl + "/" }
          ]
        }
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 md:pb-24 px-4 md:px-6 min-h-screen flex items-center bg-gradient-to-b from-[#FDFBF7] to-white">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Left: Text Content */}
            <div className="space-y-6 md:space-y-8">
              <div className="space-y-4 md:space-y-5">
                <div className="inline-block">
                  <span className="text-xs md:text-sm lg:text-base tracking-[0.15em] md:tracking-[0.2em] text-[#8B7355] font-medium uppercase" style={{ fontFamily: 'Pretendard, sans-serif' }}>세대를 연결하는 교육 경험 플랫폼</span>
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#2C2416] leading-[1.4]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                  경험을 통해<br />
                  무한한 가능성을<br />
                  발견하다
                </h1>
              </div>
              
              <div className="w-full max-w-[280px] sm:max-w-[320px] md:max-w-[360px] h-0.5 bg-[#8B7355]"></div>
              
              <div className="space-y-4 md:space-y-5">
                <p className="text-base sm:text-lg md:text-xl text-[#5A4A3A] leading-relaxed font-medium max-w-md" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                  브란치 교육 플랫폼은<br />
                  학생의 진로 탐색부터 성인들의 역량 개발까지<br />
                  모든 세대를 위한 경험 기반 교육 프로그램을 제공하여<br />
                  미래 역량과 새로운 가능성을 발견하도록 돕습니다.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4 pt-4 md:pt-6">
                <Link
                  to="/programs"
                  className="inline-flex items-center justify-center gap-2 md:gap-3 bg-[#8B7355] hover:bg-[#6d5a42] text-white px-6 md:px-9 py-3 md:py-4 rounded-full transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                >
                  <span className="font-semibold tracking-wide" style={{ fontFamily: 'Pretendard, sans-serif' }}>프로그램 알아보기</span>
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 md:gap-3 bg-[#FDFBF7] hover:bg-[#F5EFE7] text-[#8B7355] border-2 border-[#8B7355] px-6 md:px-9 py-3 md:py-4 rounded-full transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
                >
                  <span className="font-semibold tracking-wide" style={{ fontFamily: 'Pretendard, sans-serif' }}>회사소개</span>
                </Link>
              </div>
            </div>

            {/* Right: Image */}
            <div className="relative flex justify-center lg:justify-end mt-8 lg:mt-0">
              <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md">
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=group%20of%20korean%20high%20school%20students%20in%20school%20uniforms%20smiling%20brightly%20and%20looking%20at%20camera%20together%2C%20standing%20in%20front%20of%20modern%20bright%20school%20building%20entrance%2C%20warm%20sunny%20day%20with%20soft%20natural%20light%2C%20students%20showing%20genuine%20happiness%20and%20friendship%2C%20clean%20and%20bright%20background%20with%20soft%20bokeh%2C%20professional%20portrait%20photography%20style%2C%20positive%20and%20energetic%20youth%20atmosphere%2C%20east%20asian%20students%20only&width=900&height=1200&seq=hero-korean-students-new-005&orientation=portrait"
                    alt="한국 학생들의 밝은 모습"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="hidden md:block absolute -bottom-8 -right-8 w-24 md:w-32 h-24 md:h-32 bg-[#F5EFE7] rounded-full -z-10"></div>
                <div className="hidden md:block absolute -top-8 -left-8 w-20 md:w-24 h-20 md:h-24 bg-[#E8DCC8] rounded-full -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Programs Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16 lg:mb-20 space-y-4 md:space-y-5">
            <span className="text-xs md:text-sm lg:text-base tracking-[0.2em] md:tracking-[0.3em] text-[#8B7355] font-medium uppercase" style={{ fontFamily: 'Pretendard, sans-serif' }}>Our Programs</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              Branch의 핵심 프로그램
            </h2>
            <p className="text-base md:text-lg text-[#5A4A3A] font-medium" style={{ fontFamily: 'Pretendard, sans-serif' }}>차별화된 교육 경험으로 미래를 준비합니다</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {corePrograms.map((program, index) => (
              <Link
                key={program.id}
                to={`/programs#${
                  index === 0 ? 'oneday' :
                  index === 1 ? 'career-concert' :
                  index === 2 ? 'international' :
                  index === 3 ? 'career' :
                  index === 4 ? 'university' :
                  'startup'
                }`}
                className="group cursor-pointer"
                onMouseEnter={() => setActiveProgram(index)}
              >
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-4 md:mb-6">
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
                <div className="space-y-2 md:space-y-3 px-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-[#F5EFE7] group-hover:bg-[#8B7355] transition-colors">
                      <i className={`${program.icon} text-xl md:text-2xl text-[#8B7355] group-hover:text-white transition-colors`}></i>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                      {program.title}
                    </h3>
                  </div>
                  <p className="text-xs md:text-sm tracking-wider text-[#8B7355] font-medium uppercase" style={{ fontFamily: 'Pretendard, sans-serif' }}>{program.subtitle}</p>
                  <p className="text-sm md:text-base text-[#5A4A3A] leading-relaxed font-normal" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                    {program.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Experience Programs Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 md:mb-16">
            <div className="space-y-3 md:space-y-4">
              <span className="text-xs md:text-sm lg:text-base tracking-[0.2em] md:tracking-[0.3em] text-[#8B7355] font-medium uppercase" style={{ fontFamily: 'Pretendard, sans-serif' }}>Popular Experiences</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                인기 체험 프로그램
              </h2>
              <p className="text-base md:text-lg text-[#5A4A3A] font-medium" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                참가자들이 가장 많이 찾는 체험 프로그램을 소개합니다
              </p>
            </div>
            <div className="hidden md:flex items-center gap-3 flex-shrink-0 ml-8">
              <button
                onClick={handlePrev}
                disabled={slideIndex === 0}
                className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
              <button
                onClick={handleNext}
                disabled={slideIndex >= maxIndex}
                className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Slide Container */}
          <div className="overflow-hidden">
            <div
              className="flex gap-6 md:gap-8 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(calc(-${slideIndex} * (100% / ${slidesPerView} + 10.67px)))` }}
            >
              {popularExperiences.map((exp) => (
                <Link
                  key={exp.id}
                  to={exp.link}
                  className="group flex-shrink-0 cursor-pointer"
                  style={{ width: `calc((100% - ${(slidesPerView - 1) * 32}px) / ${slidesPerView})` }}
                >
                  <div className="relative overflow-hidden rounded-2xl mb-4">
                    <div className="aspect-[4/3] w-full">
                      <img
                        src={exp.image}
                        alt={exp.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#8B7355] text-white text-xs font-semibold px-3 py-1 rounded-full" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                        {exp.category}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 px-1">
                    <div className="flex items-center gap-1.5 text-[#8B7355]">
                      <i className="ri-map-pin-2-line text-sm"></i>
                      <span className="text-xs font-medium" style={{ fontFamily: 'Pretendard, sans-serif' }}>{exp.tag}</span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-[#2C2416] group-hover:text-[#8B7355] transition-colors leading-snug" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                      {exp.title}
                    </h3>
                    <p className="text-sm text-[#5A4A3A] leading-relaxed line-clamp-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                      {exp.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile nav buttons */}
          <div className="flex md:hidden items-center justify-center gap-4 mt-8">
            <button
              onClick={handlePrev}
              disabled={slideIndex === 0}
              className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
            <button
              onClick={handleNext}
              disabled={slideIndex >= maxIndex}
              className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
          </div>

          <div className="text-center mt-10 md:mt-12">
            <a
              href="https://brancheduinfo.readdy.co"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FDFBF7] hover:bg-[#F5EFE7] text-[#8B7355] border-2 border-[#8B7355] px-8 py-3.5 rounded-full transition-all whitespace-nowrap cursor-pointer font-semibold text-sm md:text-base"
              style={{ fontFamily: 'Pretendard, sans-serif' }}
            >
              <span>체험 장소 전체 보기</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 bg-[#F5EFE7]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center space-y-8 md:space-y-12">
            <div className="space-y-4 md:space-y-6">
              <span className="text-xs md:text-sm lg:text-base tracking-[0.2em] md:tracking-[0.3em] text-[#8B7355] font-medium uppercase" style={{ fontFamily: 'Pretendard, sans-serif' }}>About Branch</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2C2416] leading-[1.8] max-w-4xl mx-auto px-4" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                Branch는 &lsquo;나뭇가지&rsquo;라는 의미로,<br className="hidden sm:block" />
                각 개인이 지닌 고유한 가능성과 잠재 역량이<br className="hidden sm:block" />
                열매를 맺을 수 있도록 연결하고 지원하는 통로입니다.
              </h2>
            </div>
            
            <div className="flex justify-center">
              <div className="w-px h-16 md:h-24 bg-[#8B7355]"></div>
            </div>

            <p className="text-base md:text-lg text-[#5A4A3A] leading-relaxed font-medium max-w-2xl mx-auto px-4" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              글로벌 시민 역량을 갖춘 미래 인재 양성을 위해<br />
              차별화된 교육·문화·진로 경험을 제공합니다.
            </p>

            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[#8B7355] hover:text-[#6d5a42] text-sm md:text-base font-semibold transition-colors cursor-pointer" style={{ fontFamily: 'Pretendard, sans-serif' }}
            >
              <span>회사 더 알아보기</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16 lg:mb-20 space-y-4 md:space-y-5">
            <span className="text-xs md:text-sm lg:text-base tracking-[0.2em] md:tracking-[0.3em] text-[#8B7355] font-medium uppercase" style={{ fontFamily: 'Pretendard, sans-serif' }}>Testimonials</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2416]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              프로그램 참여자 리뷰
            </h2>
            <p className="text-base md:text-lg text-[#5A4A3A] font-medium" style={{ fontFamily: 'Pretendard, sans-serif' }}>실제 참여자들의 생생한 후기를 확인해보세요</p>
          </div>

          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

            {/* Review Slide Container */}
            <div className="overflow-hidden">
              <div
                className="flex gap-6 md:gap-8 transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(calc(-${reviewSlideIndex} * (100% / ${reviewsPerView} + 10.67px)))` }}
              >
                {reviews.map((review, index) => (
                  <div
                    key={review.id}
                    onClick={() => setSelectedReview(review.id)}
                    className={`flex-shrink-0 ${
                      index % 3 === 1 ? 'bg-white border-2 border-[#E8DCC8]' : 'bg-[#FDFBF7] opacity-90'
                    } rounded-2xl p-8 md:p-10 relative overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
                    style={{ width: `calc((100% - ${(reviewsPerView - 1) * 32}px) / ${reviewsPerView})` }}
                  >
                    <div className="absolute top-4 right-4 opacity-10">
                      <i className="ri-double-quotes-r text-6xl text-[#8B7355]"></i>
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(review.rating)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-[#F5A623] text-lg"></i>
                        ))}
                      </div>
                      <p className="text-base md:text-lg text-[#5A4A3A] leading-relaxed mb-6 line-clamp-6" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                        {review.content}
                      </p>
                      <div>
                        <p className="font-bold text-lg text-[#2C2416] mb-1" style={{ fontFamily: 'Pretendard, sans-serif' }}>{review.name}</p>
                        <p className="text-sm text-[#8B7355]" style={{ fontFamily: 'Pretendard, sans-serif' }}>{review.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-center gap-4 mt-10 md:mt-12">
              <button
                onClick={handleReviewPrev}
                disabled={reviewSlideIndex === 0}
                className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
              <button
                onClick={handleReviewNext}
                disabled={reviewSlideIndex >= maxReviewIndex}
                className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Review Modal */}
          {selectedReview !== null && (
            <div
              className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fadeIn"
              onClick={() => setSelectedReview(null)}
            >
              <div
                className="bg-white rounded-3xl p-8 md:p-12 max-w-2xl w-full relative shadow-2xl animate-scaleIn"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setSelectedReview(null)}
                  className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>

                {reviews.find(r => r.id === selectedReview) && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-1">
                      {[...Array(reviews.find(r => r.id === selectedReview)!.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#F5A623] text-2xl"></i>
                      ))}
                    </div>
                    <p className="text-xl md:text-2xl text-[#5A4A3A] leading-relaxed" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                      {reviews.find(r => r.id === selectedReview)!.content}
                    </p>
                    <div className="pt-6 border-t border-gray-200">
                      <p className="font-bold text-xl text-[#2C2416] mb-2" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                        {reviews.find(r => r.id === selectedReview)!.name}
                      </p>
                      <p className="text-base text-[#8B7355]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                        {reviews.find(r => r.id === selectedReview)!.role}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Statistics Section with Count-up Animation */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-16">
            <div className="text-center space-y-4 md:space-y-5" ref={stat1.ref}>
              <div className="text-5xl sm:text-6xl md:text-7xl font-bold text-[#8B7355]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                {stat1.count.toLocaleString()}<span className="text-4xl sm:text-5xl">+</span>
              </div>
              <div className="w-12 md:w-16 h-0.5 bg-[#8B7355] mx-auto"></div>
              <p className="text-sm md:text-base text-[#5A4A3A] font-semibold" style={{ fontFamily: 'Pretendard, sans-serif' }}>참여 학생 수</p>
            </div>
            <div className="text-center space-y-4 md:space-y-5" ref={stat2.ref}>
              <div className="text-5xl sm:text-6xl md:text-7xl font-bold text-[#8B7355]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                {stat2.count}<span className="text-4xl sm:text-5xl">+</span>
              </div>
              <div className="w-12 md:w-16 h-0.5 bg-[#8B7355] mx-auto"></div>
              <p className="text-sm md:text-base text-[#5A4A3A] font-semibold" style={{ fontFamily: 'Pretendard, sans-serif' }}>협력 교육기관</p>
            </div>
            <div className="text-center space-y-4 md:space-y-5" ref={stat3.ref}>
              <div className="text-5xl sm:text-6xl md:text-7xl font-bold text-[#8B7355]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                {stat3.count}<span className="text-4xl sm:text-5xl">+</span>
              </div>
              <div className="w-12 md:w-16 h-0.5 bg-[#8B7355] mx-auto"></div>
              <p className="text-sm md:text-base text-[#5A4A3A] font-semibold" style={{ fontFamily: 'Pretendard, sans-serif' }}>직업인 멘토 수</p>
            </div>
            <div className="text-center space-y-4 md:space-y-5" ref={stat4.ref}>
              <div className="text-5xl sm:text-6xl md:text-7xl font-bold text-[#8B7355]" style={{ fontFamily: 'Pretendard, sans-serif' }}>
                {stat4.count}<span className="text-4xl sm:text-5xl">%</span>
              </div>
              <div className="w-12 md:w-16 h-0.5 bg-[#8B7355] mx-auto"></div>
              <p className="text-sm md:text-base text-[#5A4A3A] font-semibold" style={{ fontFamily: 'Pretendard, sans-serif' }}>만족도</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 bg-[#8B7355]">
        <div className="max-w-4xl mx-auto text-center space-y-8 md:space-y-12">
          <div className="space-y-4 md:space-y-6">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.4] px-4" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              지금 바로 Branch와 함께<br />
              글로벌 역량을 키워보세요
            </h2>
            <p className="text-base md:text-lg text-white/80 font-medium" style={{ fontFamily: 'Pretendard, sans-serif' }}>
              차별화된 교육 경험이 여러분을 기다립니다
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 px-4">
            <Link
              to="/apply"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 md:gap-3 bg-white hover:bg-white/90 text-[#8B7355] px-7 md:px-9 py-3 md:py-4 rounded-full transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
            >
              <span className="font-bold tracking-wide" style={{ fontFamily: 'Pretendard, sans-serif' }}>프로그램 신청하기</span>
              <i className="ri-arrow-right-line text-lg md:text-xl"></i>
            </Link>
            <Link
              to="/support"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 md:gap-3 bg-transparent hover:bg-white/10 text-white border-2 border-white px-7 md:px-9 py-3 md:py-4 rounded-full transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl text-sm md:text-base"
            >
              <span className="font-bold tracking-wide" style={{ fontFamily: 'Pretendard, sans-serif' }}>자주 묻는 질문</span>
              <i className="ri-arrow-right-line text-lg md:text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;